/**
 * Zoea Sites — overzicht van de websites van deze organisatie.
 *
 * Woont wél in de gewone Zoea-schil: dit is een beheerpagina, geen
 * werkruimte. Pas als je een website opent, neemt de bouwer het scherm over.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, Globe, Loader2, Plus, Sparkles, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import DashboardLayout from '@/components/DashboardLayout';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { apiErrorMessage, sitesApi } from '../api';
import type { WebsiteSummary } from '../types';
import { SITE_TEMPLATES, instantiateSiteTemplate, templateChrome, type SiteTemplateId } from '../templates';

export default function SitesOverviewPage() {
  const navigate = useNavigate();
  const [websites, setWebsites] = useState<WebsiteSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [templateId, setTemplateId] = useState<SiteTemplateId>('church-home');
  const [deletingWebsite, setDeletingWebsite] = useState<WebsiteSummary | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      setWebsites(await sitesApi.listWebsites());
      setError(null);
    } catch (loadError) {
      setError(apiErrorMessage(loadError, 'De websites konden niet worden geladen.'));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim() || creating) return;
    setCreating(true);
    try {
      const created = await sitesApi.createWebsite(name.trim());
      const draft = await sitesApi.getDraft(created.websiteId);
      await sitesApi.saveDraft(created.websiteId, draft.revision, instantiateSiteTemplate(templateId), templateChrome(templateId, name.trim()));
      setDialogOpen(false);
      setName('');
      setTemplateId('church-home');
      navigate(`/sites/${created.websiteId}`);
    } catch (createError) {
      toast.error(apiErrorMessage(createError, 'De website kon niet worden aangemaakt.'));
    } finally {
      setCreating(false);
    }
  };

  const deleteWebsite = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!deletingWebsite || deleteConfirmation !== deletingWebsite.name || deleting) return;
    setDeleting(true);
    try {
      await sitesApi.deleteWebsite(deletingWebsite.websiteId);
      setWebsites((current) => current?.filter((website) => website.websiteId !== deletingWebsite.websiteId) ?? current);
      setDeletingWebsite(null);
      setDeleteConfirmation('');
      toast.success('Website verwijderd.');
    } catch (deleteError) {
      toast.error(apiErrorMessage(deleteError, 'De website kon niet worden verwijderd.'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-5xl">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-teal-700">
              <Sparkles className="h-4 w-4" /> Zoea Sites
            </p>
            <h1 className="mt-2 text-3xl font-bold tracking-tight text-slate-900">Websites</h1>
            <p className="mt-1 max-w-2xl text-slate-500">
              Bouw en onderhoud de website van je kerk. Wat je hier maakt, staat meteen klaar om te
              bekijken.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            data-testid="sites-create-website"
            className="inline-flex items-center gap-2 self-start rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700"
          >
            <Plus className="h-4 w-4" />
            Nieuwe website
          </button>
        </div>

        {error ? (
          <div
            role="alert"
            className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        ) : websites === null ? (
          <div className="flex justify-center p-16">
            <Loader2 className="h-6 w-6 animate-spin text-teal-600" />
            <span className="sr-only">Bezig met laden</span>
          </div>
        ) : websites.length === 0 ? (
          <div
            data-testid="sites-empty-state"
            className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center"
          >
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
              <Globe className="h-6 w-6" />
            </span>
            <h2 className="mt-5 text-lg font-bold text-slate-900">Nog geen website</h2>
            <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-slate-500">
              Maak je eerste website aan. Je begint met een lege homepage en voegt daar pagina&apos;s
              en tekst aan toe — precies zoals bezoekers het straks zien.
            </p>
            <button
              type="button"
              onClick={() => setDialogOpen(true)}
              className="mt-6 inline-flex items-center gap-2 rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700"
            >
              <Plus className="h-4 w-4" />
              Website maken
            </button>
          </div>
        ) : (
          <ul className="grid gap-4 sm:grid-cols-2">
            {websites.map((website) => (
              <li key={website.websiteId} className="relative">
                <Link
                  to={`/sites/${website.websiteId}`}
                  data-testid={`sites-website-${website.websiteId}`}
                  className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-5 pr-16 shadow-sm transition hover:-translate-y-0.5 hover:border-teal-300 hover:shadow-md"
                >
                  <span className="flex items-start justify-between gap-3">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600 transition group-hover:bg-teal-50 group-hover:text-teal-700">
                      <Globe className="h-5 w-5" />
                    </span>
                    <span
                      className={
                        website.isPublished
                          ? 'rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700'
                          : 'rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-500'
                      }
                    >
                      {website.isPublished ? 'Gepubliceerd' : 'Concept'}
                    </span>
                  </span>
                  <span className="mt-4 block text-base font-bold text-slate-900">
                    {website.name}
                  </span>
                  <span className="mt-1 block text-sm text-slate-500">
                    {website.pageCount === 0
                      ? "Nog geen pagina's"
                      : `${website.pageCount} ${website.pageCount === 1 ? 'pagina' : "pagina's"}`}
                  </span>
                </Link>
                <button
                  type="button"
                  aria-label={`Website ${website.name} verwijderen`}
                  data-testid={`sites-delete-website-${website.websiteId}`}
                  onClick={() => { setDeletingWebsite(website); setDeleteConfirmation(''); }}
                  className="absolute right-4 top-4 rounded-lg p-2 text-slate-400 transition hover:bg-red-50 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <form onSubmit={create}>
            <DialogHeader>
              <DialogTitle>Nieuwe website</DialogTitle>
              <DialogDescription>
                Geef de website een naam. Die is alleen zichtbaar binnen Zoea — je kunt hem later
                altijd aanpassen.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <label htmlFor="sites-new-name" className="block text-sm font-semibold text-slate-800">
                Naam
              </label>
              <input
                id="sites-new-name"
                autoFocus
                value={name}
                maxLength={120}
                onChange={(event) => setName(event.target.value)}
                placeholder="Bijvoorbeeld: Website Kerk aan de Vaart"
                className="mt-1.5 w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
              />
              <label htmlFor="sites-template" className="mt-4 block text-sm font-semibold text-slate-800">
                Start met een basis
              </label>
              <select
                id="sites-template"
                value={templateId}
                onChange={(event) => setTemplateId(event.target.value as SiteTemplateId)}
                className="mt-1.5 w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20"
              >
                {SITE_TEMPLATES.map((template) => (
                  <option key={template.id} value={template.id}>{template.name} — {template.description}</option>
                ))}
              </select>
            </div>
            <DialogFooter>
              <button
                type="button"
                onClick={() => setDialogOpen(false)}
                className="rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100"
              >
                Annuleren
              </button>
              <button
                type="submit"
                disabled={!name.trim() || creating}
                className="inline-flex items-center gap-2 rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Website maken
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deletingWebsite !== null}
        onOpenChange={(open: boolean) => {
          if (!open && !deleting) {
            setDeletingWebsite(null);
            setDeleteConfirmation('');
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <form onSubmit={deleteWebsite}>
            <DialogHeader>
              <DialogTitle>Website verwijderen?</DialogTitle>
              <DialogDescription>
                Dit verwijdert de website, het concept en de actieve development- en previewtoegang.
                Gepubliceerde versies blijven bewaard in de publicatiegeschiedenis.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-slate-700">
                Typ <span className="font-bold">{deletingWebsite?.name}</span> om door te gaan.
              </p>
              <input
                autoFocus
                value={deleteConfirmation}
                onChange={(event) => setDeleteConfirmation(event.target.value)}
                aria-label="Naam van de website ter bevestiging"
                className="mt-2 w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20"
              />
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setDeletingWebsite(null)} disabled={deleting} className="rounded-xl px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-100">
                Annuleren
              </button>
              <button
                type="submit"
                disabled={!deletingWebsite || deleteConfirmation !== deletingWebsite.name || deleting}
                className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Definitief verwijderen
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
